// Piston push/pull resolution (RED-3, M16.1). PURE (ENG-1): no three/DOM/audio.
// Given a read-only view of the world, decide which blocks a piston moves when it
// extends or (sticky) retracts — the classic Minecraft PistonStructureResolver,
// with the push limit, immovable/destroy move classes, and slime/honey adhesion.
//
// The host (boot) supplies the view (block names + a move-class + an adhesion
// classifier) and enacts the returned plan: it snapshots each `from` cell's state,
// writes every `to`, then clears the vacated `from`s, and breaks the `destroyed`
// cells. Because the host writes all destinations before clearing sources, the
// move order is irrelevant — overlapping cells resolve correctly either way.
import { REDSTONE } from '../config';
import { ALL_DIRS, DIR_VEC, OPPOSITE, type Dir } from './redstone/types';

/** How a block responds to being pushed. */
export type MoveClass =
  | 'normal' // rides the piston (solid, movable)
  | 'immovable' // stops the whole push (obsidian, block entities, extended pistons…)
  | 'destroy'; // crushed and dropped when a mover enters its cell (plants, torches, fluids)

/** A block's stickiness for adhesion. slime and honey stick to everything they
 *  touch — except each other (slime ⇄ honey never stick). */
export type Adhesion = 'none' | 'slime' | 'honey';

/** The read-only world the resolver classifies. */
export interface PistonView {
  /** Block name at a cell ('air' for empty). */
  name(x: number, y: number, z: number): string;
  moveClass(x: number, y: number, z: number): MoveClass;
  adhesion(x: number, y: number, z: number): Adhesion;
}

/** One block relocation: the state at (from…) moves to (to…). */
export interface Move {
  fromX: number; fromY: number; fromZ: number;
  toX: number; toY: number; toZ: number;
}

/** The blocks a piston action moves + the replaceable cells it crushes. */
export interface PushPlan {
  moves: Move[];
  destroyed: Array<[number, number, number]>;
}

type Cell = readonly [number, number, number];
const key = (c: Cell): string => `${c[0]},${c[1]},${c[2]}`;

/** slime and honey repel each other; any other sticky pairing adheres. */
function repel(a: Adhesion, b: Adhesion): boolean {
  return (a === 'slime' && b === 'honey') || (a === 'honey' && b === 'slime');
}

/** Breadth-first grow of the moving structure from `seed` along `moveDir`,
 *  dragging adhesion-connected neighbours. Returns the cells to move, or null if
 *  the push is blocked (an immovable block is in the structure, or it exceeds the
 *  limit). The piston base (`baseKey`) is never pulled into the structure. */
function grow(
  view: PistonView, seed: Cell, moveDir: Dir, baseKey: string, limit: number,
): Cell[] | null {
  const [mx, my, mz] = DIR_VEC[moveDir];
  const seen = new Set<string>();
  const toMove: Cell[] = [];
  const queue: Cell[] = [seed];
  while (queue.length) {
    const c = queue.shift()!;
    const k = key(c);
    if (seen.has(k) || k === baseKey) continue;
    if (view.name(c[0], c[1], c[2]) === 'air') continue; // air isn't part of the structure
    const cls = view.moveClass(c[0], c[1], c[2]);
    if (cls === 'destroy') continue; // crushed, not moved (recorded by computeDestroyed)
    if (cls === 'immovable') return null; // the whole push fails
    seen.add(k);
    toMove.push(c);
    if (toMove.length > limit) return null;
    // Every mover shoves the block directly ahead of it (the push chain).
    queue.push([c[0] + mx, c[1] + my, c[2] + mz]);
    // A sticky block drags each of its 6 face neighbours (except a repelling pair).
    const ad = view.adhesion(c[0], c[1], c[2]);
    if (ad !== 'none') {
      for (const d of ALL_DIRS) {
        const [dx, dy, dz] = DIR_VEC[d];
        const nb: Cell = [c[0] + dx, c[1] + dy, c[2] + dz];
        if (seen.has(key(nb))) continue;
        if (!repel(ad, view.adhesion(nb[0], nb[1], nb[2]))) queue.push(nb);
      }
    }
  }
  return toMove;
}

/** Replaceable cells a mover moves into (crushed + dropped). */
function computeDestroyed(view: PistonView, toMove: readonly Cell[], moveDir: Dir): Array<[number, number, number]> {
  const [mx, my, mz] = DIR_VEC[moveDir];
  const moving = new Set(toMove.map(key));
  const out: Array<[number, number, number]> = [];
  for (const c of toMove) {
    const dest: Cell = [c[0] + mx, c[1] + my, c[2] + mz];
    if (moving.has(key(dest))) continue;
    if (view.name(dest[0], dest[1], dest[2]) === 'air') continue;
    if (view.moveClass(dest[0], dest[1], dest[2]) === 'destroy') out.push([dest[0], dest[1], dest[2]]);
  }
  return out;
}

function mv(c: Cell, dir: Dir): Move {
  const [dx, dy, dz] = DIR_VEC[dir];
  return { fromX: c[0], fromY: c[1], fromZ: c[2], toX: c[0] + dx, toY: c[1] + dy, toZ: c[2] + dz };
}

/** An entity's world-space box, used by the piston entity-shove (E-14). */
export interface Aabb {
  minX: number; minY: number; minZ: number;
  maxX: number; maxY: number; maxZ: number;
}

/** The cells a piston action newly fills — its moved blocks' destinations plus the
 *  extending head cell. Anything standing in these gets shoved out (E-14). */
export function occupiedAfter(plan: PushPlan, headX: number, headY: number, headZ: number): Cell[] {
  const cells: Cell[] = plan.moves.map((m) => [m.toX, m.toY, m.toZ] as Cell);
  cells.push([headX, headY, headZ]);
  return cells;
}

/**
 * The displacement that pushes `box` clear of every piston-occupied cell along
 * `moveDir` (E-14 entity/player shove). A cell (cx,cy,cz) fills the unit box
 * [cx,cx+1]³. The shove is the *minimal* slide along +moveDir that lifts the box
 * past the frontmost overlapping cell — a box resting on a moved block (touching,
 * not overlapping) is left alone, and a box overlapping nothing gets a zero vector.
 * The host adds this to the entity's position (and zeroes its along-axis velocity)
 * so the extending block shoulders it aside instead of clipping through it.
 */
export function pistonPushBox(occupied: readonly Cell[], moveDir: Dir, box: Aabb): { x: number; y: number; z: number } {
  const [mx, my, mz] = DIR_VEC[moveDir];
  const axis = mx !== 0 ? 0 : my !== 0 ? 1 : 2;
  const sign = mx + my + mz; // ±1 along the single move axis
  const bMin = [box.minX, box.minY, box.minZ];
  const bMax = [box.maxX, box.maxY, box.maxZ];
  let push = 0;
  for (const [cx, cy, cz] of occupied) {
    const cMin = [cx, cy, cz];
    const cMax = [cx + 1, cy + 1, cz + 1];
    if (!(bMax[0]! > cMin[0]! && bMin[0]! < cMax[0]! &&
          bMax[1]! > cMin[1]! && bMin[1]! < cMax[1]! &&
          bMax[2]! > cMin[2]! && bMin[2]! < cMax[2]!)) continue;
    const need = sign > 0 ? cMax[axis]! - bMin[axis]! : bMax[axis]! - cMin[axis]!;
    if (need > push) push = need;
  }
  if (push <= 0) return { x: 0, y: 0, z: 0 };
  return { x: mx * push, y: my * push, z: mz * push };
}

/**
 * Resolve a piston at (px,py,pz) facing `dir` extending. Returns the plan, or
 * null if it cannot extend (an immovable block blocks it, or the moved structure
 * exceeds `limit`). An empty plan (no moves, no destroyed) means the front cell is
 * clear and the head simply fills it.
 */
export function resolveExtend(
  view: PistonView, px: number, py: number, pz: number, dir: Dir,
  limit: number = REDSTONE.PISTON_PUSH_LIMIT,
): PushPlan | null {
  const [fx, fy, fz] = DIR_VEC[dir];
  const start: Cell = [px + fx, py + fy, pz + fz];
  if (view.name(start[0], start[1], start[2]) !== 'air') {
    const cls = view.moveClass(start[0], start[1], start[2]);
    if (cls === 'immovable') return null;
    if (cls === 'destroy') return { moves: [], destroyed: [[start[0], start[1], start[2]]] };
  }
  const toMove = grow(view, start, dir, key([px, py, pz]), limit);
  if (!toMove) return null;
  if (toMove.length === 0) return { moves: [], destroyed: [] }; // front was air
  return { moves: toMove.map((c) => mv(c, dir)), destroyed: computeDestroyed(view, toMove, dir) };
}

/**
 * Resolve a piston at (px,py,pz) facing `dir` retracting. A non-sticky piston
 * pulls nothing. A sticky piston pulls the block just beyond its (now removed)
 * head back into the head cell, dragging any adhesion-connected structure — but
 * only if that block is a normal solid mover and the pull stays within `limit`
 * (otherwise the piston retracts and leaves the block behind). Retracting into
 * open space never destroys anything.
 */
export function resolveRetract(
  view: PistonView, px: number, py: number, pz: number, dir: Dir, sticky: boolean,
  limit: number = REDSTONE.PISTON_PUSH_LIMIT,
): PushPlan {
  const empty: PushPlan = { moves: [], destroyed: [] };
  if (!sticky) return empty;
  const [fx, fy, fz] = DIR_VEC[dir];
  const pull: Cell = [px + 2 * fx, py + 2 * fy, pz + 2 * fz];
  if (view.name(pull[0], pull[1], pull[2]) === 'air') return empty;
  if (view.moveClass(pull[0], pull[1], pull[2]) !== 'normal') return empty; // only pull solid movers
  const back = OPPOSITE[dir];
  const toMove = grow(view, pull, back, key([px, py, pz]), limit);
  if (!toMove || toMove.length === 0) return empty;
  return { moves: toMove.map((c) => mv(c, back)), destroyed: [] };
}
